'use client';

/**
 * BudgetAlertWatcher — bridges server-side budget alerts into the in-app
 * notification bell.
 *
 * The bell (useNotificationStore) is a client-side store with no server push,
 * and budget threshold crossings (80% warn / 100% crit) are persisted to
 * maple.observability_alert_events (surfaced on /monitor/budgets). This watcher
 * polls the open budget alerts and pushes any it hasn't seen into the bell,
 * deduped by alert_event_id via localStorage.
 *
 * First run per browser seeds the "seen" set SILENTLY so a login doesn't flood
 * the bell with pre-existing alerts — only alerts that appear afterward notify.
 * Fail-soft: any error is swallowed; visibility-gated to avoid background churn.
 */
import { useEffect, useRef } from 'react';
import apiClient from '@/lib/api-client';
import { useAuthStore } from '@/store/auth-store';
import { useNotificationStore } from '@/store/notification-store';

const SEEN_KEY = 'uzo-budget-alert-seen';
const POLL_MS = 60_000;

export function BudgetAlertWatcher() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const raw = typeof window !== 'undefined' ? localStorage.getItem(SEEN_KEY) : null;
    const isFirstRun = raw === null;
    const seen = new Set<string>(raw ? JSON.parse(raw) : []);

    const persist = () => {
      try { localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-500))); } catch { /* ignore */ }
    };

    const poll = async (silent = false) => {
      try {
        // Background poll: it must never raise the global error toast. It runs
        // on every page for every signed-in user, so a user without
        // observability.read got "You need the observability.read permission"
        // on the home page right after login.
        const res = await apiClient.get('/monitor/alerts', { params: { status: 'open', category: 'budget' }, skipGlobalErrorToast: true });
        const alerts: any[] = res.data?.alerts || [];
        let changed = false;
        for (const a of alerts) {
          if (!a.alert_event_id || seen.has(a.alert_event_id)) continue;
          seen.add(a.alert_event_id);
          changed = true;
          if (!silent) {
            addNotification({
              type: a.severity === 'critical' ? 'error' : 'warning',
              title: a.title || 'Budget alert',
              message: a.summary || '',
              link: '/monitor/budgets',
            });
          }
        }
        if (changed || isFirstRun) persist();
      } catch (err: any) {
        // No access (403) or gone (404) will not change on the next poll —
        // stop asking until the next sign-in instead of failing every minute.
        const st = err?.response?.status;
        if ((st === 403 || st === 404) && timer.current) { clearInterval(timer.current); timer.current = null; }
      }
    };

    // First run seeds silently (no flood of historical alerts); later polls notify.
    poll(isFirstRun);
    timer.current = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') poll(false);
    }, POLL_MS);

    return () => { if (timer.current) clearInterval(timer.current); };
  }, [isAuthenticated, addNotification]);

  return null;
}
